<script setup>
  import {computed, inject, nextTick, ref, watch} from "vue";

  import ComponentEditor from "@components/Editor/Editor.vue";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";
  import Window from "@utils/Helpers/Window.js";
  import anchorme from "anchorme";

  defineOptions({name: "PagesRecordResultListClaimTextBox"});

  const props = defineProps({
    mcCheckId: {
      type: Number,
      required: true,
    },
    problem: {
      type: String,
      required: true,
    },
    solution: {
      type: String,
      required: true,
    },
    problemOriginal: {
      type: String,
      required: true,
    },
    solutionOriginal: {
      type: String,
      required: true,
    },
    comment: {
      type: String,
      default: () => {
        return "";
      },
    },
    claimNumber: {
      type: Number,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    allowShortening: {
      type: Boolean,
      default: true,
    },
  });

  const emit = defineEmits(["update:problem", "update:solution", "open:coreInfoDialog", "activate:communication"]);

  const addToastMessage = inject("addToastMessage");

  const {validatePrivacy} = usePrivacyGuard();

  const showComment = ref(false);

  const solutionTitle = ref("Lösungsvorschlag");

  const refProblemEditor = ref(null);
  const refSolutionEditor = ref(null);
  const refSolution = ref(null);

  const internalProblem = ref(props.problem);
  const internalSolution = ref(props.solution);

  // create || init
  if (props.mcCheckId === 20) {
    solutionTitle.value = "Tipps";
  }

  const solutionLengthIsShortened = ref(false);

  watch(
    () => props.visible,
    (isVisible) => {
      if (isVisible) {
        // recalculate, if 'cline-clamp' class shortens the solution <div>
        nextTick(() => {
          solutionLengthIsShortened.value = refSolution.value?.scrollHeight > refSolution.value?.offsetHeight;
        });
      }
    },
  );

  watch(
    () => [props.problem, props.solution],
    ([newProblem, newSolution], [oldProblem, oldSolution]) => {
      if (newProblem !== oldProblem) {
        internalProblem.value = newProblem;
        if (
          !props.readOnly &&
          normalizeClaimTextToPlainText(newProblem) !== normalizeClaimTextToPlainText(oldProblem)
        ) {
          // only emit communication when normalized (!) text has been changed
          emit("activate:communication");
        }
      }
      if (newSolution !== oldSolution) {
        internalSolution.value = newSolution;
        if (
          !props.readOnly &&
          normalizeClaimTextToPlainText(newSolution) !== normalizeClaimTextToPlainText(oldSolution)
        ) {
          emit("activate:communication");
        }
      }
    },
  );

  /**
   * Replaces html entities (&nbsp;) and removes tags
   * and blanks for a content-only comparison.
   */
  function normalizeClaimTextToPlainText(html) {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value
      .replaceAll("<br>", " ")
      .replaceAll(/<[^>]*>/g, "")
      .replaceAll(/\s+/g, " ")
      .trim();
  }

  const solutionLengthHasBeenMaximised = ref(!props.allowShortening);

  function toggleSolutionLength() {
    if (solutionLengthIsShortened.value && !solutionLengthHasBeenMaximised.value) {
      solutionLengthHasBeenMaximised.value = true;
    } else {
      solutionLengthHasBeenMaximised.value = !solutionLengthHasBeenMaximised.value;
      // recalculate
      nextTick(() => {
        solutionLengthIsShortened.value = refSolution.value?.scrollHeight > refSolution.value?.offsetHeight;
      });
    }
  }

  /**
   * An empty claim text must never reach the parent form: ListClaimItem auto-saves every
   * change after 800ms, so an empty value would silently overwrite the stored claim text.
   * Besides a user emptying the editor, the content can also be cleared by a foreign DOM
   * mutation (browser extensions, see the slot observer in Editor.vue) - therefore we keep
   * the parent state untouched instead of propagating the empty value.
   */
  function claimTextIsEmpty(claimText) {
    return (claimText ?? "").trim().length === 0;
  }

  function blurProblemEditor() {
    if (claimTextIsEmpty(internalProblem.value)) {
      addToastMessage("Das Problem kann nicht leer sein, der Originaltext wurde wiederhergestellt.", "error", 5000);
      internalProblem.value = props.problemOriginal;
    }

    // safety net: do not emit if even the restored original text is empty
    if (claimTextIsEmpty(internalProblem.value)) {
      return;
    }

    emit("update:problem", internalProblem.value);
  }

  function blurSolutionEditor() {
    if (claimTextIsEmpty(internalSolution.value)) {
      addToastMessage(
        "Der Lösungsvorschlag kann nicht leer sein, der Originaltext wurde wiederhergestellt.",
        "error",
        5000,
      );
      internalSolution.value = props.solutionOriginal;
    }

    // safety net: do not emit if even the restored original text is empty
    if (claimTextIsEmpty(internalSolution.value)) {
      return;
    }

    emit("update:solution", internalSolution.value);
  }

  function checkForEmbeddedLink(eventObject, callback = null) {
    // the click target may be an element nested inside the link (e.g. <a><em>…</em></a>)
    const link = eventObject.target.closest("a");

    if (link) {
      eventObject.stopPropagation();
      eventObject.preventDefault();

      if (Object.keys(link.dataset).length === 0) {
        // external links (without any data attribute)
        Window.open(link.href, "_blank");
      } else {
        // handleMedicalTextLink
        const datasetNames = Object.keys(link.dataset);

        // new: layer detail content is linked within href
        if (datasetNames.includes("layer")) {
          // href="https://www..." data-layer="interaction|dosage|..."
          emit("open:coreInfoDialog", {
            url: link.href,
            type: link.dataset.layer,
          });
        }
        // legacy: layer detail content url will be generated via data-attribute
        // (for claims which are already stored in our dataservice)
        else if (datasetNames.includes("keyfat")) {
          emit("open:coreInfoDialog", {
            referenceName: link.dataset.famnames || null,
            key: link.dataset.keyfat,
            type: "dosage",
          });
        } else if (datasetNames.includes("keyint")) {
          emit("open:coreInfoDialog", {
            referenceName: null,
            key: link.dataset.keyint,
            type: "interaction",
          });
        } else if (datasetNames.includes("guidelineid")) {
          emit("open:coreInfoDialog", {
            referenceName: null,
            key: link.dataset.guidelineid,
            medicationType: link.dataset.guidelinemedication,
            type: "guideline",
          });
        }
      }
    } else if (callback) {
      callback();
    }
  }

  const privacyErrors = computed(() => {
    return {
      internalProblem: validatePrivacy(internalProblem, "Text"),
      internalSolution: validatePrivacy(internalSolution, "Lösungsvorschlag"),
    };
  });

  // auto-link plain URLs in the comment; existing <a> tags remain untouched (skipHTML default)
  const renderedComment = computed(() =>
    anchorme({
      input: props.comment ?? "",
      options: {
        attributes: {target: "_blank", rel: "noopener noreferrer"},
      },
    }),
  );
</script>

<template>
  <!-- eslint-disable vue/no-v-html -- every v-html in this file renders claim rich text: internalProblem and internalSolution are Trix editor content (see ComponentEditor bindings below), renderedComment is anchorme output that exists to produce anchors -->
  <div class="claim">
    <template v-if="readOnly">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- delegation container for the anchors inside the rendered claim HTML; those anchors are real tabbable links and their keyboard activation dispatches a bubbling click -->
      <div
        :class="cn('-mx-1 px-2 py-1', {'hover:bg-yellow-50': !readOnly})"
        @click="(event) => checkForEmbeddedLink(event)"
        v-html="internalProblem"
      />
    </template>

    <template v-else>
      <ComponentEditor
        v-if="visible"
        ref="refProblemEditor"
        v-model="internalProblem"
        class="claim-text min-h-full print:hidden"
        :privacy-guard="privacyErrors.internalProblem ?? undefined"
        :test-id="getTestId('claim-problem')"
        @blur="blurProblemEditor()"
        @click="(event) => checkForEmbeddedLink(event)"
      />
      <div class="-mx-1 hidden px-2 py-1 print:block" v-html="internalProblem" />
    </template>

    <div :class="cn('px-1 font-bold text-green-700 print:mt-6 print:break-after-avoid-page', {'mt-6': readOnly})">
      {{ solutionTitle }}:
    </div>

    <div>
      <template v-if="readOnly">
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- delegation container for the anchors inside the rendered claim HTML; those anchors are real tabbable links and their keyboard activation dispatches a bubbling click -->
        <div
          ref="refSolution"
          :class="
            cn('-mx-1 bg-green-50/50 px-2 py-1 print:line-clamp-none print:break-inside-auto', {
              'hover:bg-yellow-50': !readOnly,
              'line-clamp-4': !solutionLengthHasBeenMaximised,
            })
          "
          :data-test="getTestId('claim-edit-internal-solution')"
          @click="(event) => checkForEmbeddedLink(event)"
          v-html="internalSolution"
        />
      </template>

      <template v-else>
        <ComponentEditor
          v-if="visible"
          ref="refSolutionEditor"
          v-model="internalSolution"
          class="claim-text min-h-full print:hidden"
          editor-class="bg-green-50/50"
          :privacy-guard="privacyErrors.internalSolution ?? undefined"
          :test-id="getTestId('claim-internal-solution')"
          @blur="blurSolutionEditor()"
          @click="(event) => checkForEmbeddedLink(event)"
        />
        <div class="-mx-1 hidden px-2 py-1 print:block" v-html="internalSolution" />
      </template>

      <div class="flex justify-between px-1 print:hidden">
        <div v-if="solutionLengthIsShortened || solutionLengthHasBeenMaximised">
          <button
            v-if="solutionLengthIsShortened && !solutionLengthHasBeenMaximised"
            class="cursor-pointer text-mcred hover:underline"
            :data-test="getTestId('claim-internal-solution-read-more')"
            @click="toggleSolutionLength()"
          >
            mehr lesen
          </button>
          <button
            v-else-if="allowShortening"
            class="cursor-pointer text-mcred hover:underline"
            @click="toggleSolutionLength()"
          >
            weniger lesen
          </button>
        </div>

        <div v-else><!-- nix --></div>
      </div>
    </div>

    <div class="flex justify-between">
      <div v-if="comment && comment.length > 0" class="mt-2 px-1 print:hidden">
        <button
          class="cursor-pointer text-mcred hover:underline"
          :data-test="getTestId('claim-show-comment')"
          @click="showComment = !showComment"
        >
          Zusatzinformationen / Quellen
        </button>

        <div
          v-if="showComment"
          class="text-gray-600 italic [&_a]:text-mcred [&_a]:hover:underline"
          v-html="renderedComment"
        />
      </div>

      <div v-if="$slots['ai-button']" class="flex gap-1">
        <slot name="ai-button" />
      </div>
    </div>
  </div>
</template>

<style>
  @reference "@css/app.css";

  /* unfortunately it's not possible to @apply our custom 'list-dash' tailwind plugin */
  .claim ul {
    @apply my-1 pl-4;
    list-style-type: "- ";
  }

  .claim ol {
    @apply my-1 pl-5;
    list-style: decimal;
  }

  .claim a {
    color: var(--color-mcred-500);
    text-decoration-color: var(--color-mcred-500);
    cursor: pointer;
  }
</style>
